import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { TaskImage } from '../../../../../services/openapi/models/TaskImage';
import { TaskDocument } from '../../../../../services/openapi/models/TaskDocument';
import { TaskWithImages } from '../hooks/useTaskDetail';
import { AttachmentList } from './AttachmentList';

interface TaskAttachmentsProps {
  task: TaskWithImages | null;
  uploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  formatDate: (dateString: string) => string;
  getDocumentIcon: (mimeType: string, filename?: string) => React.ReactElement;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleImageClick: (image: TaskImage) => void;
  handleEditImage: (image: TaskImage) => void;
  handleCropImage: (image: TaskImage) => void;
  handleDownloadImage: (image: TaskImage) => void;
  handleDeleteImage: (imageId: number) => void;
  handleDownloadDocument: (document: TaskDocument) => void;
  handleShareDocument: (document: TaskDocument) => void;
  handleDeleteDocument: (documentId: number) => void;
  // Invoice creation props
  userRole?: string[];
  onCreateInvoice?: (document: TaskDocument) => void;
}

export const TaskAttachments: React.FC<TaskAttachmentsProps> = ({
  task,
  uploading,
  fileInputRef,
  formatDate,
  getDocumentIcon,
  handleFileUpload,
  handleImageClick,
  handleEditImage,
  handleCropImage,
  handleDownloadImage,
  handleDeleteImage,
  handleDownloadDocument,
  handleShareDocument,
  handleDeleteDocument,
  userRole,
  onCreateInvoice,
}) => {
  const totalAttachments = (task?.images?.length || 0) + (task?.documents?.length || 0);

  // Check if user can create invoices (Administrator or Administrator_Rechnung)
  const canCreateInvoice = userRole?.includes('Administrator') || userRole?.includes('Administrator_Rechnung');

  const handleImageAction = (action: (image: TaskImage) => void) => (item: any) => {
    const image = task?.images?.find(img => img.id === item.id);
    if (image) action(image);
  };

  const handleDocumentAction = (action: (document: TaskDocument) => void) => (item: any) => {
    const document = task?.documents?.find(doc => doc.id === item.id);
    if (document) action(document);
  };

  const handleCreateInvoiceAction = (item: any) => {
    const document = task?.documents?.find(doc => doc.id === item.id);
    if (document && onCreateInvoice) {
      onCreateInvoice(document);
    }
  };

  const attachmentItems = [
    ...(task?.images?.map(image => ({
      id: image.id,
      type: 'image' as const,
      name: `Bild #${image.id}`,
      original_filename: (image as any).original_filename,
      filename: (image as any).original_filename,
      uploaded_at: image.uploaded_at,
      image_path: image.image_path,
    })) || []),
    ...(task?.documents?.map(document => ({
      id: document.id,
      type: 'document' as const,
      name: document.filename,
      filename: document.filename,
      uploaded_at: (document as any).created_at || (document as any).uploaded_at || new Date().toISOString(),
      mime_type: document.mime_type,
      file_size: document.file_size,
      document_type: (document as any).document_type,
    })) || [])
  ];

  return (
    <Card variant='outlined' sx={{ mb: 3 }}>
      <CardContent>
        <Box display='flex' alignItems='center' gap={1} mb={2}>
          <AttachFileIcon color='primary' />
          <Typography variant='h6'>Anhänge ({totalAttachments})</Typography>
        </Box>

        {/* Upload Button */}
        <Box sx={{ mb: 3 }}>
          <input
            type='file'
            ref={fileInputRef}
            style={{ display: 'none' }}
            multiple
            accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt'
            onChange={handleFileUpload}
          />
          <Button
            variant='outlined'
            startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                boxShadow: 'none',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {uploading ? 'Wird hochgeladen...' : 'Dateien hinzufügen'}
          </Button>
        </Box>

        {/* Attachments List */}
        {totalAttachments > 0 ? (
          <AttachmentList
            items={attachmentItems}
            getDocumentIcon={(mimeType, filename) => getDocumentIcon(mimeType || '', filename)}
            formatDate={formatDate}
            onImageClick={handleImageAction(handleImageClick)}
            onEditImage={handleImageAction(handleEditImage)}
            onCropImage={handleImageAction(handleCropImage)}
            onDownloadImage={handleImageAction(handleDownloadImage)}
            onDeleteImage={(id) => handleDeleteImage(id as number)}
            onDownloadDocument={handleDocumentAction(handleDownloadDocument)}
            onShareDocument={handleDocumentAction(handleShareDocument)}
            onDeleteDocument={(id) => handleDeleteDocument(id as number)}
            showEditActions={true}
            showCropActions={true}
            showCreateInvoice={canCreateInvoice}
            onCreateInvoice={handleCreateInvoiceAction}
          />
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 4,
              bgcolor: 'grey.50',
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 3,
              color: 'text.secondary',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.50',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <AttachFileIcon sx={{ fontSize: 32, color: 'grey.500' }} />
              </Box>
              <Typography variant='h6' fontWeight={600} color="text.primary">
                Keine Anhänge vorhanden
              </Typography>
              <Typography variant='body2' sx={{ maxWidth: 300 }}>
                Klicken Sie auf "Dateien hinzufügen", um Bilder oder Dokumente hochzuladen
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
